import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { TextStyle as TextStyleExtension } from '@tiptap/extension-text-style';
import { Color as ColorExtension } from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
// @ts-ignore
import Image from '@tiptap/extension-image';
import { Box, ToggleButton, ToggleButtonGroup, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from '@mui/icons-material';

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  height?: number | string;
  scale?: number;
}

// Font families removed - using Times New Roman as default

const MenuBar = ({ editor }: { editor: any }) => {
  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        p: 1,
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiToggleButtonGroup-root': {
          height: 30,
        },
        '& .MuiToggleButton-root': {
          padding: '4px 8px',
        },
      }}
    >
      {/* Font selector removed */}

      <ToggleButtonGroup size='small'>
        <ToggleButton value='bold' selected={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <FormatBold />
        </ToggleButton>
        <ToggleButton value='italic' selected={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <FormatItalic />
        </ToggleButton>
        <ToggleButton
          value='underline'
          selected={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FormatUnderlined />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation='vertical' flexItem />

      <ToggleButtonGroup size='small'>
        <ToggleButton
          value='bulletList'
          selected={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulleted />
        </ToggleButton>
        <ToggleButton
          value='orderedList'
          selected={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FormatListNumbered />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation='vertical' flexItem />

      <ToggleButtonGroup size='small' exclusive>
        <ToggleButton
          value='left'
          selected={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <FormatAlignLeft />
        </ToggleButton>
        <ToggleButton
          value='center'
          selected={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <FormatAlignCenter />
        </ToggleButton>
        <ToggleButton
          value='right'
          selected={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <FormatAlignRight />
        </ToggleButton>
        <ToggleButton
          value='justify'
          selected={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        >
          <FormatAlignJustify />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, height = 500, scale = 1 }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            class: 'paragraph',
          },
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      TextStyleExtension,
      ColorExtension,
      FontFamily,
      Image,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none',
      },
    },
  });

  // Update editor content when value prop changes
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // Font family change handler removed

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: 1,
        '.ProseMirror': {
          height: typeof height === 'number' ? `${height - 52}px` : height,
          overflowY: 'auto',
          padding: '20px',
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          fontSize: '12px',
          lineHeight: 1.4,
          fontFamily: 'Times New Roman, serif',
          '&:focus': {
            outline: 'none',
          },
          '& *::selection': {
            background: 'rgba(0, 0, 0, 0.1)',
          },
          '& *:focus': {
            outline: 'none',
          },
          '& p': {
            margin: '0.3em 0',
            '&:focus': {
              outline: 'none',
              background: 'transparent',
            },
            '&::selection': {
              background: 'rgba(0, 0, 0, 0.1)',
            },
          },
          '& h2': {
            fontSize: '14px !important',
            fontWeight: 'bold',
            margin: '0.5em 0',
          },
          '& h3': {
            fontSize: '13px !important',
            fontWeight: 'bold',
            margin: '0.5em 0',
          },
          'ul, ol': {
            padding: '0 1rem',
          },
          img: {
            maxWidth: '100%',
            height: 'auto',
          },
          // Remove default browser outline
          '*[contenteditable="true"]': {
            outline: 'none !important',
            '&:focus': {
              outline: 'none !important',
              background: 'transparent !important',
            },
          },
        },
      }}
    >
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </Box>
  );
};

export default RichTextEditor;
